import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, Trash2, Shield, User, RefreshCw, Download, Upload, Archive, History, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [selectedTaskVersions, setSelectedTaskVersions] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    name: '',
    password: '',
    role: 'kunde'
  });

  useEffect(() => {
    fetchUsers();
    fetchArchivedTasks();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Benutzer');
    }
  };

  const fetchArchivedTasks = async () => {
    try {
      const response = await axios.get(`${API}/admin/archived-tasks`, {
        headers: getAuthHeader()
      });
      setArchivedTasks(response.data);
    } catch (error) {
      console.error('Fehler beim Laden archivierter Tasks:', error);
    }
  };

  const fetchTaskVersions = async (taskId) => {
    try {
      const response = await axios.get(`${API}/admin/tasks/${taskId}/versions`, {
        headers: getAuthHeader()
      });
      setSelectedTaskVersions(response.data);
      setSelectedTaskId(taskId);
      setIsVersionDialogOpen(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Versionen');
    }
  };

  const handleRestoreVersion = async (taskId, versionId, versionNumber) => {
    if (!window.confirm(`Möchten Sie den Task wirklich zu Version ${versionNumber} wiederherstellen?`)) return;
    try {
      await axios.post(`${API}/admin/tasks/${taskId}/versions/${versionId}/restore`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Task zu Version ${versionNumber} wiederhergestellt!`);
      setIsVersionDialogOpen(false);
    } catch (error) {
      toast.error('Fehler beim Wiederherstellen der Version');
    }
  };

  const handleRestoreArchivedTask = async (archiveId) => {
    if (!window.confirm('Möchten Sie diesen Task wiederherstellen?')) return;
    try {
      await axios.post(`${API}/admin/archived-tasks/${archiveId}/restore`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Task wurde wiederhergestellt!');
      fetchArchivedTasks();
    } catch (error) {
      toast.error('Fehler beim Wiederherstellen des Tasks');
    }
  };

  const handlePermanentlyDeleteTask = async (archiveId) => {
    if (!window.confirm('Möchten Sie diesen Task ENDGÜLTIG löschen? Diese Aktion kann nicht rückgängig gemacht werden!')) return;
    try {
      await axios.delete(`${API}/admin/archived-tasks/${archiveId}`, {
        headers: getAuthHeader()
      });
      toast.success('Task wurde endgültig gelöscht!');
      fetchArchivedTasks();
    } catch (error) {
      toast.error('Fehler beim Löschen des Tasks');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register-by-admin`, newUser, {
        headers: getAuthHeader()
      });
      toast.success('Benutzer erfolgreich erstellt!');
      setIsCreateDialogOpen(false);
      setNewUser({ username: '', email: '', name: '', password: '', role: 'kunde' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Benutzers');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Möchten Sie diesen Benutzer wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: getAuthHeader()
      });
      toast.success('Benutzer gelöscht!');
      fetchUsers();
    } catch (error) {
      toast.error('Fehler beim Löschen des Benutzers');
    }
  };

  const handleToggleRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'kunde' : 'admin';
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole }, {
        headers: getAuthHeader()
      });
      toast.success(`Rolle zu ${newRole === 'admin' ? 'Administrator' : 'Kunde'} geändert!`);
      fetchUsers();
    } catch (error) {
      toast.error('Fehler beim Ändern der Rolle');
    }
  };

  const handleExport = async () => {
    try {
      toast.info('Export wird vorbereitet...');
      const response = await axios.get(`${API}/admin/export`, {
        headers: getAuthHeader()
      });
      
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Daten erfolgreich exportiert!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Fehler beim Exportieren der Daten');
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('import_file', file);

      const response = await axios.post(`${API}/admin/import`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(
        `Import erfolgreich! ${response.data.imported.users} Benutzer, ${response.data.imported.projects} Projekte, ${response.data.imported.tasks} Tasks importiert.`
      );
      fetchUsers();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Importieren der Daten');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f7fb]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                data-testid="back-to-dashboard-button"
                onClick={() => navigate('/')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Zurück</span>
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">Admin-Panel</h1>
                  <p className="text-sm text-gray-500">Verwaltung & Wiederherstellung</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleExport}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </Button>
              <Button
                onClick={handleImportClick}
                disabled={isImporting}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>{isImporting ? 'Importiere...' : 'Import'}</span>
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImport}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Tabs */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="users" className="flex items-center space-x-2" data-testid="tab-users">
              <User className="w-4 h-4" />
              <span>Benutzer</span>
            </TabsTrigger>
            <TabsTrigger value="archive" className="flex items-center space-x-2" data-testid="tab-archive">
              <Archive className="w-4 h-4" />
              <span>Archiv ({archivedTasks.length})</span>
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="flex justify-end mb-4">
              <Button
                data-testid="create-user-button"
                onClick={() => setIsCreateDialogOpen(true)}
                className="bg-[#0073ea] hover:bg-[#0060c0] text-white flex items-center space-x-2"
              >
                <UserPlus className="w-4 h-4" />
                <span>Neuer Benutzer</span>
              </Button>
            </div>
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#f6f7fb] border-b-2 border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Benutzername</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">E-Mail</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Rolle</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {users.map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center">
                              <span className="text-white text-sm font-semibold">
                                {u.username.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-medium text-gray-800">{u.username}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600">{u.name || '-'}</td>
                        <td className="px-6 py-4 text-gray-600">{u.email}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.role === 'admin' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {u.role === 'admin' ? 'Administrator' : 'Kunde'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`toggle-role-${u.id}`}
                              onClick={() => handleToggleRole(u.id, u.role)}
                              className="text-gray-500 hover:text-purple-600"
                              title="Rolle wechseln"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`delete-user-${u.id}`}
                              onClick={() => handleDeleteUser(u.id)}
                              className="text-red-500 hover:text-red-700"
                              title="Benutzer löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Archive Tab */}
          <TabsContent value="archive">
            <Card className="overflow-hidden">
              {archivedTasks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Archive className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Keine archivierten Tasks vorhanden</p>
                  <p className="text-sm mt-1">Gelöschte Tasks werden hier zur Wiederherstellung angezeigt</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#f6f7fb] border-b-2 border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Task</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Projekt</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Gelöscht von</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Gelöscht am</th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {archivedTasks.map((archived) => (
                        <tr key={archived.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-800">{archived.task_data?.title || 'Unbekannt'}</p>
                              <p className="text-sm text-gray-500 truncate max-w-xs">
                                {archived.task_data?.description || 'Keine Beschreibung'}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-600">{archived.project_name || '-'}</td>
                          <td className="px-6 py-4 text-gray-600">{archived.deleted_by_username}</td>
                          <td className="px-6 py-4 text-gray-600">
                            {new Date(archived.deleted_at).toLocaleString('de-DE')}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => fetchTaskVersions(archived.original_task_id)}
                                className="text-blue-500 hover:text-blue-700"
                                title="Versionsverlauf anzeigen"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRestoreArchivedTask(archived.id)}
                                className="text-green-500 hover:text-green-700"
                                title="Task wiederherstellen"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePermanentlyDeleteTask(archived.id)}
                                className="text-red-500 hover:text-red-700"
                                title="Endgültig löschen"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]" aria-describedby="dialog-create-user-description">
          <DialogHeader>
            <DialogTitle>Neuen Benutzer erstellen</DialogTitle>
            <DialogDescription id="dialog-create-user-description">
              Füllen Sie das Formular aus, um einen neuen Benutzer zu erstellen.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="username">Benutzername *</Label>
              <Input
                id="username"
                data-testid="new-user-username-input"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                required
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="email">E-Mail *</Label>
              <Input
                id="email"
                type="email"
                data-testid="new-user-email-input"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                data-testid="new-user-name-input"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="password">Passwort *</Label>
              <Input
                id="password"
                type="password"
                data-testid="new-user-password-input"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
                className="mt-2"
              />
            </div>
            <div>
              <Label>Rolle</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger className="mt-2" data-testid="new-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kunde">Kunde</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" data-testid="submit-new-user-button" className="bg-[#0073ea] hover:bg-[#0060c0] text-white">
                Erstellen
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={isVersionDialogOpen} onOpenChange={setIsVersionDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto" aria-describedby="dialog-versions-description">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <History className="w-5 h-5" />
              <span>Versionsverlauf</span>
            </DialogTitle>
            <DialogDescription id="dialog-versions-description">
              Wählen Sie eine Version aus, um den Task wiederherzustellen.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {selectedTaskVersions.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                Keine Versionen für diesen Task verfügbar
              </p>
            ) : (
              selectedTaskVersions.map((version) => (
                <div
                  key={version.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-1 rounded">
                          Version {version.version_number}
                        </span>
                        <span className="text-sm text-gray-500">
                          von {version.changed_by_username}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{version.change_summary}</p>
                      <p className="text-xs text-gray-400">
                        {new Date(version.created_at).toLocaleString('de-DE')}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestoreVersion(selectedTaskId, version.id, version.version_number)}
                      className="ml-4 flex items-center space-x-1"
                    >
                      <RotateCcw className="w-3 h-3" />
                      <span>Wiederherstellen</span>
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
